import { useMemo, useState } from "react";
import { Text } from "@ttc3k/trekker";
import {
  EnumProductTierSessionScope,
  EnumProductType,
  PurchasedProductGuestsPerTier,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { format, parse } from "date-fns";
import { Flex } from "styled-system/jsx";
import { SimpleFlag } from "shared/components";
import { formatPrice, getMultilingualString } from "shared/utils";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { CollapsableStep, UpsellSelectionCard } from "widgets/booker/shared";
import { useEventPurchaseFlow } from "./useEventPurchaseFlow";
import { EventDaySelector } from "./EventDaySelector";
import { EventTimeSlotSelector } from "./EventTimeSlotSelector";
import { EventFlexSessionPicker } from "./EventFlexSessionPicker";
import { shouldShowLastFew } from "shared/merchCatalog";

const Scope = EnumProductTierSessionScope;

export const EventTierSelectorStep = () => {
  const { t } = useTranslation("booker", { keyPrefix: "EVENT_TIER_SELECTOR" });
  const { product, language, readOnlySelection } = useBookerContext();
  const { cart, addCartItem, updateCartItem, removeCartItem } = useCartContext();
  const {
    flow,
    sellingConfig,
    days,
    sessionsByDate,
    bookableSessions,
    hasTimeSlots,
    isDaySoldOut,
    isToday,
    isTomorrow,
    hasNoFutureSessions,
    availabilityBySessionId,
  } = useEventPurchaseFlow();

  const [activeDay, setActiveDay] = useState<string>("");
  const [sessionId, setSessionId] = useState<string>("");
  const [sessionIds, setSessionIds] = useState<string[]>([]);

  if (flow !== "tierSelector" || !product) return null;

  const productId = product._id;
  const options = sellingConfig.options;
  // All tiers in a packages product share one scope (set by the selling type).
  const scope = (options[0]?.sessionScope ?? Scope.Single) as
    | EnumProductTierSessionScope;
  const requiredCount =
    options[0]?.minSessions ?? options[0]?.maxSessions ?? 0;

  const mainCartItem = cart.find(item => item.productId === productId);
  const guestTiers = mainCartItem?.guestsPerTier ?? [];
  const maxTicketsPerPurchase =
    product.eventMetadata?.maxTicketsPerPurchase ?? undefined;
  const totalSelected = guestTiers.reduce(
    (sum, tier) => sum + (tier.count ?? 0),
    0,
  );

  // A single-session event (e.g. a Full pass / whole-event) needs no picker.
  const soleSessionId =
    bookableSessions.length === 1 ? bookableSessions[0].sessionId : null;

  const needsSinglePicker = scope === Scope.Single && !soleSessionId;
  const needsFlexPicker = scope === Scope.PickN;

  const fullPassDateLabel = useMemo(() => {
    if (scope !== Scope.All || days.length === 0) return "";
    const parsed = days.map(d => parse(d, "yyyy-MM-dd", new Date()));
    if (parsed.length === 1) return format(parsed[0], "MMMM d, yyyy");
    const first = parsed[0];
    const last = parsed[parsed.length - 1];
    const sameMonth =
      first.getMonth() === last.getMonth() &&
      first.getFullYear() === last.getFullYear();
    if (sameMonth) {
      return `${format(first, "MMMM d")} – ${format(last, "d, yyyy")}`;
    }
    const sameYear = first.getFullYear() === last.getFullYear();
    if (sameYear) {
      return `${format(first, "MMMM d")} – ${format(last, "MMMM d, yyyy")}`;
    }
    return `${format(first, "MMMM d, yyyy")} – ${format(last, "MMMM d, yyyy")}`;
  }, [scope, days]);

  const effectiveSessionId = soleSessionId ?? sessionId;

  // Quantity steppers stay disabled until the required session(s) are chosen.
  const sessionReady =
    scope === Scope.Single
      ? Boolean(effectiveSessionId)
      : scope === Scope.PickN
        ? requiredCount > 0 && sessionIds.length === requiredCount
        : true;

  // Sessions this purchase consumes capacity in (every ticket reserves one seat
  // in each). The shared cap is the lowest remaining among them.
  const coveredSessionIds =
    scope === Scope.All
      ? bookableSessions.map(session => session.sessionId)
      : scope === Scope.PickN
        ? sessionIds
        : effectiveSessionId
          ? [effectiveSessionId]
          : [];

  const remainingFor = (sessionId: string): number => {
    const availability = availabilityBySessionId[sessionId];
    if (!availability || availability.isUnlimited) return Number.MAX_SAFE_INTEGER;
    return availability.remainingCapacity ?? Number.MAX_SAFE_INTEGER;
  };

  const coveredRemaining =
    coveredSessionIds.length === 0
      ? Number.MAX_SAFE_INTEGER
      : Math.min(...coveredSessionIds.map(remainingFor));

  // Smallest total capacity among the covered sessions: a tiny session is always
  // "low", so the nudge is only meaningful when capacity is at least the threshold.
  const coveredTotalCapacity =
    coveredSessionIds.length === 0
      ? Number.MAX_SAFE_INTEGER
      : Math.min(
          ...coveredSessionIds.map(sessionId => {
            const availability = availabilityBySessionId[sessionId];
            if (!availability || availability.isUnlimited) {
              return Number.MAX_SAFE_INTEGER;
            }
            return availability.totalCapacity ?? Number.MAX_SAFE_INTEGER;
          }),
        );

  const isSoldOut = coveredRemaining <= 0;
  const isLimited = shouldShowLastFew(coveredRemaining, coveredTotalCapacity);

  const sessionFieldsFor = (): Partial<PurchasedProductGuestsPerTier> => {
    if (scope === Scope.Single) return { sessionId: effectiveSessionId };
    if (scope === Scope.PickN) return { sessionIds };
    return {};
  };

  const commit = (counts: Record<string, number>) => {
    const sessionFields = sessionFieldsFor();
    const nextGuestTiers: PurchasedProductGuestsPerTier[] = options
      .map(tier => ({
        tierId: tier._id,
        count: counts[tier._id] ?? 0,
        ...sessionFields,
      }))
      .filter(tier => (tier.count ?? 0) > 0);

    const quantity = nextGuestTiers.reduce(
      (sum, tier) => sum + (tier.count ?? 0),
      0,
    );

    if (quantity === 0) {
      removeCartItem(productId);
      return;
    }

    const firstSession =
      nextGuestTiers[0]?.sessionId ?? nextGuestTiers[0]?.sessionIds?.[0] ?? "";
    const firstDate = firstSession.split("::")[0] ?? "";
    const payload = {
      guestsPerTier: nextGuestTiers,
      quantity,
      startDateISO: firstDate,
      endDateISO: firstDate,
    };

    if (mainCartItem) {
      updateCartItem(productId, payload);
      return;
    }

    addCartItem({
      productId,
      productName: getMultilingualString(product.name, language),
      productType: EnumProductType.Event,
      total: 0,
      ...payload,
    });
  };

  const currentCounts = Object.fromEntries(
    guestTiers.map(tier => [tier.tierId, tier.count ?? 0]),
  );

  const setTierQuantity = (tierId: string, count: number) => {
    commit({ ...currentCounts, [tierId]: count });
  };

  // Re-apply a changed session selection to any tiers already chosen.
  const reapplySession = () => {
    if (totalSelected > 0) commit(currentCounts);
  };

  const allToday = days.length > 0 && days.every(d => isToday(d));
  const allTomorrow =
    !allToday && days.length > 0 && days.every(d => isTomorrow(d));

  return (
    <CollapsableStep
      title={readOnlySelection ? t("TITLE_READONLY") : t("TITLE")}
      complete={totalSelected > 0}
      stepId="event-tier"
    >
      {!readOnlySelection && <Text color="text.mid">{t("SUBTITLE")}</Text>}
      {hasNoFutureSessions && (
        <SimpleFlag text={t("NO_FUTURE_SESSIONS")} visual="warning" />
      )}
      {allToday && !hasNoFutureSessions && (
        <SimpleFlag text={t("HAPPENING_TODAY")} visual="accent.blue" />
      )}
      {allTomorrow && !hasNoFutureSessions && (
        <SimpleFlag text={t("HAPPENING_TOMORROW")} visual="accent.blue" />
      )}

      <Flex flexDirection="column" gap="200">
        {/* Shared day/slot picker */}
        {needsSinglePicker && (
          <Flex flexDirection="column" gap="150">
            <Text color="text.dark" visual="smallMedium">
              {hasTimeSlots ? t("CHOOSE_DAY") : t("CHOOSE_DATE")}
            </Text>
            <EventDaySelector
              days={days}
              value={
                hasTimeSlots
                  ? activeDay
                    ? [activeDay]
                    : []
                  : effectiveSessionId
                    ? [effectiveSessionId]
                    : []
              }
              onChange={next => {
                if (hasTimeSlots) {
                  setActiveDay(next[0] ?? "");
                } else {
                  setSessionId(next[0] ?? "");
                  setTimeout(reapplySession, 0);
                }
              }}
              isDaySoldOut={isDaySoldOut}
              isToday={isToday}
              isTomorrow={isTomorrow}
              todayLabel={t("HAPPENING_TODAY")}
              tomorrowLabel={t("HAPPENING_TOMORROW")}
              language={language}
              placeholder={t("DATE_PLACEHOLDER")}
            />
            {hasTimeSlots && activeDay && (
              <>
                <Text color="text.dark" visual="smallMedium">
                  {t("CHOOSE_TIME")}
                </Text>
                <EventTimeSlotSelector
                  slots={sessionsByDate[activeDay] ?? []}
                  value={effectiveSessionId ? [effectiveSessionId] : []}
                  onChange={next => {
                    setSessionId(next[0] ?? "");
                    setTimeout(reapplySession, 0);
                  }}
                  availabilityBySessionId={availabilityBySessionId}
                />
              </>
            )}
          </Flex>
        )}

        {needsFlexPicker && (
          <EventFlexSessionPicker
            requiredCount={requiredCount}
            value={sessionIds}
            onChange={next => {
              setSessionIds(next);
              setTimeout(reapplySession, 0);
            }}
            days={days}
            sessionsByDate={sessionsByDate}
            hasTimeSlots={hasTimeSlots}
            isDaySoldOut={isDaySoldOut}
            isToday={isToday}
            todayLabel={t("HAPPENING_TODAY")}
            availabilityBySessionId={availabilityBySessionId}
            language={language}
          />

        )}

        {/* Tier quantity steppers */}
        <Flex flexDirection="column" gap="150">
          {fullPassDateLabel && (
            <Text color="text.mid" visual="bodyMedium">
              {fullPassDateLabel}
            </Text>
          )}
          {(needsSinglePicker || needsFlexPicker) && (
            <Text color="text.dark" visual="smallMedium">
              {t("CHOOSE_TICKET_TYPE")}
            </Text>
          )}
          {options.map(tier => {
            const description = getMultilingualString(tier.description ?? {});
            const currentQty = currentCounts[tier._id] ?? 0;
            const tierMaxPerPurchase =
              tier.quantity?.enabled && tier.quantity.maxQuantity != null
                ? tier.quantity.maxQuantity
                : undefined;
            // Product-level max and session capacity are shared across tiers;
            // this option's max applies only to its own quantity.
            const sharedCap = Math.min(
              maxTicketsPerPurchase ?? Number.MAX_SAFE_INTEGER,
              coveredRemaining,
            );
            const fromShared =
              sharedCap >= Number.MAX_SAFE_INTEGER
                ? undefined
                : currentQty + Math.max(sharedCap - totalSelected, 0);
            const maxQuantity =
              tierMaxPerPurchase != null && fromShared != null
                ? Math.min(fromShared, tierMaxPerPurchase)
                : (tierMaxPerPurchase ?? fromShared);
            const atTierMax =
              tierMaxPerPurchase != null && currentQty >= tierMaxPerPurchase;
            const isOrderSinglePick = maxTicketsPerPurchase === 1;
            const isSinglePick = isOrderSinglePick || tierMaxPerPurchase === 1;
            // With one ticket per order, switching on another option replaces
            // the current pick instead of being blocked by the shared cap.
            const toggleTier = (on: boolean) => {
              if (on && isOrderSinglePick) {
                commit({ [tier._id]: 1 });
                return;
              }
              setTierQuantity(tier._id, on ? 1 : 0);
            };
            const toggleBlocked =
              !isOrderSinglePick && currentQty === 0 && maxQuantity === 0;

            return (
              <Flex key={tier._id} flexDirection="column" gap="100">
                <UpsellSelectionCard
                  mainText={getMultilingualString(tier.name)}
                  subText={`$${formatPrice(tier.price)}`}
                  quantityEnabled={!isSinglePick}
                  quantity={currentQty}
                  minQuantity={0}
                  maxQuantity={maxQuantity}
                  checked={currentQty > 0}
                  disabled={
                    readOnlySelection ||
                    hasNoFutureSessions ||
                    !sessionReady ||
                    (isSoldOut && currentQty === 0) ||
                    (isSinglePick && toggleBlocked)
                  }
                  onCheckedChange={toggleTier}
                  onQuantityChange={count => setTierQuantity(tier._id, count)}
                  footer={
                    sessionReady && isSoldOut && currentQty === 0 ? (
                      <SimpleFlag text={t("SOLD_OUT")} visual="destructive" />
                    ) : sessionReady && atTierMax && !isSinglePick ? (
                      <SimpleFlag
                        text={t("MAX_PER_PURCHASE", {
                          count: tierMaxPerPurchase,
                        })}
                        visual="destructive"
                      />
                    ) : sessionReady && isLimited ? (
                      <SimpleFlag
                        text={t("LIMITED_AVAILABILITY", {
                          count: coveredRemaining,
                        })}
                        visual="warning"
                      />
                    ) : undefined
                  }
                />
                {description ? (
                  <Text color="text.mid" visual="smallRegular" fontStyle="italic">
                    {description}
                  </Text>
                ) : null}
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    </CollapsableStep>
  );
};
